import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  IconButton,
  Typography,
  Box,
} from "@mui/material";
import { ContentCopy, Close } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { toast } from "react-hot-toast";

interface CredentialsPopupProps {
  open: boolean;
  onClose: () => void;
  credentials: {
    login: string;
    password: string;
    organizationName: string;
  };
}

const CredentialsPopup: React.FC<CredentialsPopupProps> = ({
  open,
  onClose,
  credentials,
}) => {
  const { t } = useTranslation();
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const handleCopy = async (text: string, fieldName: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(fieldName);
      toast.success(t("COMMON.COPIED_SUCCESSFULLY"));
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      toast.error(t("COMMON.COPY_FAILED"));
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          padding: 2,
        },
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" component="div">
            {t("COMMON.CREDENTIALS_CREATED")}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box mb={3}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {t("COMMON.ORGANIZATION_CREATED_SUCCESSFULLY")}: <strong>{credentials.organizationName}</strong>
          </Typography>
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Box display="flex" alignItems="center" gap={1}>
              <TextField
                label={t("COMMON.LOGIN")}
                value={credentials.login}
                fullWidth
                InputProps={{
                  readOnly: true,
                }}
                variant="outlined"
              />
              <IconButton
                onClick={() => handleCopy(credentials.login, "login")}
                color={copiedField === "login" ? "success" : "primary"}
                size="small"
              >
                <ContentCopy />
              </IconButton>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Box display="flex" alignItems="center" gap={1}>
              <TextField
                label={t("COMMON.PASSWORD")}
                value={credentials.password}
                fullWidth
                InputProps={{
                  readOnly: true,
                }}
                variant="outlined"
                type="password"
              />
              <IconButton
                onClick={() => handleCopy(credentials.password, "password")}
                color={copiedField === "password" ? "success" : "primary"}
                size="small"
              >
                <ContentCopy />
              </IconButton>
            </Box>
          </Grid>
        </Grid>

        <Box mt={2}>
          <Typography variant="body2" color="warning.main">
            {t("COMMON.SAVE_CREDENTIALS_WARNING")}
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="contained" fullWidth>
          {t("COMMON.CLOSE")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CredentialsPopup;
